import { useAuth } from "@contexts/auth";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ActionIcon,
  Avatar,
  Badge,
  Button,
  Group,
  Menu,
  Modal,
  Pagination,
  Paper,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconArchive,
  IconDots,
  IconEye,
  IconMailForward,
  IconPencil,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconUserCheck,
} from "@tabler/icons-react";
import dayjs from "dayjs";

import { OnboardingInvitationAccess } from "@components/OnboardingInvitationAccess";

import {
  useProspect,
  useProspectAction,
  useProspects,
  useProspectSummary,
} from "@hooks/queries/accountManagement/useAccountManagement";

import {
  OnboardingInvitationCredentials,
  Prospect,
  ProspectInvitationStatus,
  ProspectStatus,
} from "@customTypes/accountManagement";

import { ProspectDetailsModal } from "./ProspectDetailsModal";
import { ProspectFormModal } from "./ProspectFormModal";


const pipelineStages: { value: ProspectStatus; label: string }[] = [
  { value: "prospect", label: "Prospects" },
  { value: "contacted", label: "Contacted" },
  { value: "invitation_sent", label: "Invitation Sent" },
  { value: "account_created", label: "Accounts Created" },
  { value: "onboarding", label: "Onboarding" },
  { value: "live", label: "Live" },
];

const statusColors: Record<ProspectStatus, string> = {
  prospect: "gray",
  contacted: "blue",
  invitation_sent: "indigo",
  account_created: "violet",
  onboarding: "orange",
  live: "green",
};

const invitationLabels: Record<ProspectInvitationStatus, string> = {
  not_sent: "Not sent",
  active: "Active",
  expired: "Expired",
  in_progress: "In progress",
  completed: "Completed",
  rejected: "Rejected",
};

const invitationColors: Record<ProspectInvitationStatus, string> = {
  not_sent: "gray",
  active: "blue",
  expired: "red",
  in_progress: "orange",
  completed: "green",
  rejected: "gray",
};

const leadSourceLabel = (value: string) =>
  value === "public_doctor_suggestion"
    ? "Public doctor suggestion"
    : value.replaceAll("_", " ");

const Metric = ({
  label,
  value,
  hint,
}: {
  label: string;
  value: string | number;
  hint?: string;
}) => (
  <Paper withBorder radius="md" p="md">
    <Text size="xs" c="dimmed">
      {label}
    </Text>
    <Text size="xl" fw={700} c="indigo.9">
      {value}
    </Text>
    {hint && (
      <Text size="xs" c="dimmed">
        {hint}
      </Text>
    )}
  </Paper>
);

export const RecruitmentTab = () => {
  const { can } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedProspectId = searchParams.get("prospectId") || "";
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<ProspectStatus | "">("");
  const [accountType, setAccountType] = useState<"" | "doctor" | "organization">(
    ""
  );
  const [leadSource, setLeadSource] = useState("");
  const [referrerType, setReferrerType] = useState<"doctor" | "recruiter" | "">("");
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState<Prospect | null>(null);
  const [statusNote, setStatusNote] = useState("");
  const [formOpened, form] = useDisclosure(false);
  const [detailsOpened, details] = useDisclosure(false);
  const [statusOpened, statusModal] = useDisclosure(false);
  const [invitationOpened, invitationModal] = useDisclosure(false);
  const [invitationResult, setInvitationResult] =
    useState<OnboardingInvitationCredentials | null>(null);
  const summary = useProspectSummary();
  const prospects = useProspects({
    page,
    pageSize: 20,
    search,
    status: statusFilter,
    accountType,
    leadSource,
    referrerType,
    archived: showArchived,
  });
  const action = useProspectAction();
  const linkedProspect = useProspect(linkedProspectId, Boolean(linkedProspectId));

  useEffect(() => {
    if (linkedProspect.data) {
      setSelected(linkedProspect.data);
      details.open();
    }
  }, [linkedProspect.data]);

  const runAction = (
    prospect: Prospect,
    operation:
      | "contact"
      | "invite"
      | "renew-invite"
      | "archive"
      | "restore"
  ) => {
    action
      .mutateAsync({ prospectId: prospect._id, action: operation })
      .then(result => {
        if (
          ["invite", "renew-invite"].includes(operation) &&
          "token" in result
        ) {
          setInvitationResult(result);
          invitationModal.open();
          return;
        }
        notifications.show({
          color: "green",
          message: {
            contact: "Prospect marked as contacted.",
            invite: "Invitation created.",
            "renew-invite": "Invitation renewed for another 30 days.",
            archive: "Prospect archived.",
            restore: "Prospect restored.",
          }[operation],
        });
      })
      .catch(() =>
        notifications.show({
          color: "red",
          message: "The action could not be completed.",
        })
      );
  };

  const rows = useMemo(
    () =>
      (prospects.data?.items || []).map(prospect => (
        <Table.Tr key={prospect._id}>
          <Table.Td>
            <Group gap="sm" wrap="nowrap">
              <Avatar size={32} color="violet">
                {(prospect.name || prospect.account_type)
                  .slice(0, 2)
                  .toUpperCase()}
              </Avatar>
              <div>
                <Text size="sm" fw={500}>
                  {prospect.name || "—"}
                </Text>
                <Text size="xs" c="dimmed">
                  {prospect.specialty_or_department || prospect.account_type}
                </Text>
                {prospect.public_suggestion_count > 0 && (
                  <Badge size="xs" variant="light" color="teal" mt={4}>
                    {prospect.public_suggestion_count === 1
                      ? "1 public suggestion"
                      : `${prospect.public_suggestion_count} public suggestions`}
                  </Badge>
                )}
              </div>
            </Group>
          </Table.Td>
          <Table.Td>
            <Text size="sm">{prospect.phone_number || "—"}</Text>
          </Table.Td>
          <Table.Td>
            <Text size="sm">{leadSourceLabel(prospect.lead_source)}</Text>
          </Table.Td>
          <Table.Td>
            <Text size="sm">Self-onboard</Text>
          </Table.Td>
          <Table.Td>
            <Text size="sm">{prospect.referrer_name || "—"}</Text>
            {prospect.referrer_type && <Text size="xs" c="dimmed">{prospect.referrer_type} · {prospect.referral_plan_name || "—"}{prospect.promised_reward_cfa ? ` · ${prospect.promised_reward_cfa.toLocaleString()} CFA${prospect.reward_amount_status === "estimated" ? " (estimated)" : ""}` : ""}</Text>}
          </Table.Td>
          <Table.Td>
            <Badge
              variant="light"
              color={statusColors[prospect.status]}
              tt="capitalize">
              {prospect.status.replaceAll("_", " ")}
            </Badge>
          </Table.Td>
          <Table.Td>
            <Stack gap={2} align="flex-start">
              <Badge
                variant="light"
                color={invitationColors[prospect.invitation_status]}>
                {invitationLabels[prospect.invitation_status]}
              </Badge>
              {prospect.invitation_expires_at &&
                ["active", "expired", "in_progress"].includes(
                  prospect.invitation_status
                ) && (
                  <Text size="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
                    {prospect.invitation_status === "expired"
                      ? "Expired"
                      : "Expires"}{" "}
                    {dayjs(prospect.invitation_expires_at).format("D MMM YYYY")}
                  </Text>
                )}
            </Stack>
          </Table.Td>
          <Table.Td>
            <Text size="sm">{prospect.identified_by_name || "—"}</Text>
          </Table.Td>
          <Table.Td>
            <Text size="sm">
              {dayjs(prospect.identified_at).format("D MMM YYYY")}
            </Text>
          </Table.Td>
          <Table.Td>
            <Menu shadow="md" width={190} position="bottom-end">
              <Menu.Target>
                <ActionIcon variant="subtle" aria-label="Prospect actions">
                  <IconDots size={18} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconEye size={16} />}
                  onClick={() => {
                    setSelected(prospect);
                    details.open();
                  }}>
                  View details
                </Menu.Item>
                <Menu.Item
                  disabled={!can("recruitment.edit")} leftSection={<IconPencil size={16} />}
                  onClick={() => {
                    setSelected(prospect);
                    form.open();
                  }}>
                  Edit details
                </Menu.Item>
                {!prospect.archived_at && prospect.status === "prospect" && (
                  <Menu.Item
                    disabled={!can("recruitment.edit")} leftSection={<IconUserCheck size={16} />}
                    onClick={() => {
                      setSelected(prospect);
                      setStatusNote("");
                      statusModal.open();
                    }}>
                    Mark contacted
                  </Menu.Item>
                )}
                {!prospect.archived_at &&
                  ["prospect", "contacted"].includes(prospect.status) && (
                    <Menu.Item
                      disabled={!can("users.invite") || (prospect.account_type !== "doctor" && !can("access.manage"))} leftSection={<IconMailForward size={16} />}
                      onClick={() => runAction(prospect, "invite")}>
                      Send invitation
                    </Menu.Item>
                  )}
                {!prospect.archived_at &&
                  prospect.invitation_status === "expired" && (
                    <Menu.Item
                      leftSection={<IconRefresh size={16} />}
                      disabled={!can("users.invite") || (prospect.account_type !== "doctor" && !can("access.manage"))} onClick={() => runAction(prospect, "renew-invite")}>
                      Renew invitation
                    </Menu.Item>
                  )}
                <Menu.Divider />
                {prospect.archived_at ? (
                  <Menu.Item
                    leftSection={<IconRefresh size={16} />}
                    disabled={!can("recruitment.archive")} onClick={() => runAction(prospect, "restore")}>
                    Restore prospect
                  </Menu.Item>
                ) : (
                  <Menu.Item
                    color="red"
                    leftSection={<IconArchive size={16} />}
                    disabled={!can("recruitment.archive")} onClick={() => runAction(prospect, "archive")}>
                    Archive prospect
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          </Table.Td>
        </Table.Tr>
      )),
    [prospects.data?.items, can]
  );

  const data = summary.data;
  return (
    <Stack gap="lg" pb={120}>
      <Group justify="space-between">
        <div>
          <Title order={3}>Recruitment</Title>
          <Text size="sm" c="dimmed">
            Manage doctor and organization prospects through onboarding.
          </Text>
        </div>
        <Button
          leftSection={<IconPlus size={16} />}
          disabled={!can("recruitment.create")} onClick={() => {
            setSelected(null);
            form.open();
          }}>
          Add Prospect
        </Button>
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        <Metric label="Total Prospects" value={data?.total_prospects || 0} />
        <Metric label="Live" value={data?.live || 0} />
        <Metric label="Onboarding" value={data?.onboarding || 0} />
        <Metric
          label="Avg. Onboarding Time"
          value={
            data?.average_onboarding_days != null
              ? `${data.average_onboarding_days} days`
              : "—"
          }
          hint="From identification to live"
        />
      </SimpleGrid>

      <Paper withBorder radius="md" p="md">
        <Title order={4} mb="md">
          Recruitment Pipeline
        </Title>
        <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }}>
          {pipelineStages.map(stage => (
            <Paper
              key={stage.value}
              p="sm"
              radius="md"
              bg={statusFilter === stage.value ? "violet.0" : "gray.0"}
              onClick={() => {
                setStatusFilter(current =>
                  current === stage.value ? "" : stage.value
                );
                setPage(1);
              }}
              style={{ cursor: "pointer" }}>
              <Text ta="center" fw={700}>
                {data?.pipeline?.[stage.value] || 0}
              </Text>
              <Text ta="center" size="xs" c="dimmed">
                {stage.label}
              </Text>
            </Paper>
          ))}
        </SimpleGrid>
      </Paper>

      <Paper withBorder radius="md" p="md">
        <Group justify="space-between" mb="md">
          <Title order={4}>{showArchived ? "Archived Prospects" : "Prospects"}</Title>
          <Button
            size="xs"
            variant="subtle"
            onClick={() => {
              setShowArchived(value => !value);
              setPage(1);
            }}>
            {showArchived ? "View active prospects" : "View archive"}
          </Button>
        </Group>
        <Group align="end" mb="md">
          <TextInput
            label="Search"
            placeholder="Search prospects"
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={event => {
              setSearch(event.currentTarget.value);
              setPage(1);
            }}
            style={{ flex: 1, minWidth: 220 }}
          />
          <Select
            label="Account type"
            clearable
            data={[
              { value: "doctor", label: "Doctors" },
              { value: "organization", label: "Organizations" },
            ]}
            value={accountType || null}
            onChange={value => {
              setAccountType(
                (value || "") as "" | "doctor" | "organization"
              );
              setPage(1);
            }}
          />
          <Select
            label="Pipeline status"
            clearable
            data={pipelineStages}
            value={statusFilter || null}
            onChange={value => {
              setStatusFilter((value || "") as ProspectStatus | "");
              setPage(1);
            }}
          />
          <Select
            label="Lead source"
            clearable
            data={[
              "Doctor referral",
              "Patient referral",
              "Social media",
              "Event",
              "Website",
              "Outbound",
              "Other",
              {
                value: "public_doctor_suggestion",
                label: "Public doctor suggestion",
              },
            ]}
            value={leadSource || null}
            onChange={value => {
              setLeadSource(value || "");
              setPage(1);
            }}
          />
          <Select
            label="Referrer type"
            clearable
            data={[{ value: "doctor", label: "Doctor" }, { value: "recruiter", label: "Recruiter" }]}
            value={referrerType || null}
            onChange={value => { setReferrerType((value || "") as typeof referrerType); setPage(1); }}
          />
        </Group>

        <ScrollArea>
          <Table miw={1250} highlightOnHover>
            <Table.Thead bg="gray.0">
              <Table.Tr>
                <Table.Th>Prospect</Table.Th>
                <Table.Th>Phone Number</Table.Th>
                <Table.Th>Lead Source</Table.Th>
                <Table.Th>Method</Table.Th>
                <Table.Th>Referrer / Plan</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Invitation</Table.Th>
                <Table.Th>Identified By</Table.Th>
                <Table.Th>Date Identified</Table.Th>
                <Table.Th>Action</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {prospects.isLoading ? (
                <Table.Tr>
                  <Table.Td colSpan={10}>
                    <Text ta="center" c="dimmed" py="xl">
                      Loading prospects…
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : prospects.isError ? (
                <Table.Tr>
                  <Table.Td colSpan={10}>
                    <Text ta="center" c="red" py="xl">
                      Prospects could not be loaded.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : rows.length ? (
                rows
              ) : (
                <Table.Tr>
                  <Table.Td colSpan={10}>
                    <Text ta="center" c="dimmed" py="xl">
                      No prospects match these filters.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
        {(prospects.data?.total || 0) > 20 && (
          <Group justify="flex-end" mt="md">
            <Pagination
              value={page}
              onChange={setPage}
              total={Math.ceil((prospects.data?.total || 0) / 20)}
            />
          </Group>
        )}
      </Paper>

      <ProspectFormModal
        opened={formOpened && can(selected ? "recruitment.edit" : "recruitment.create")}
        onClose={form.close}
        prospect={selected}
      />
      <ProspectDetailsModal
        opened={detailsOpened}
        onClose={() => {
          details.close();
          if (linkedProspectId) {
            const params = new URLSearchParams(searchParams);
            params.delete("prospectId");
            setSearchParams(params, { replace: true });
          }
        }}
        prospect={selected}
      />
      <Modal
        opened={invitationOpened}
        onClose={() => {
          invitationModal.close();
          setInvitationResult(null);
        }}
        title="Invitation ready"
        centered>
        {invitationResult && (
          <OnboardingInvitationAccess
            invitation={invitationResult}
            showSuccessHeader
            onDone={() => {
              invitationModal.close();
              setInvitationResult(null);
            }}
          />
        )}
      </Modal>
      <Modal
        opened={statusOpened && can("recruitment.edit")}
        onClose={statusModal.close}
        title="Update Pipeline Status"
        centered>
        <Stack>
          <Group justify="space-between">
            <div>
              <Text size="xs" c="dimmed">
                Current status
              </Text>
              <Badge variant="light" color="gray">
                Prospect
              </Badge>
            </div>
            <Text>→</Text>
            <div>
              <Text size="xs" c="dimmed">
                New status
              </Text>
              <Badge variant="light" color="blue">
                Contacted
              </Badge>
            </div>
          </Group>
          <Textarea
            label="Note"
            placeholder="Optional context about the contact"
            value={statusNote}
            onChange={event => setStatusNote(event.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={statusModal.close}>
              Cancel
            </Button>
            <Button
              loading={action.isLoading}
              onClick={() => {
                if (!selected) return;
                action
                  .mutateAsync({
                    prospectId: selected._id,
                    action: "contact",
                    note: statusNote || undefined,
                  })
                  .then(() => {
                    notifications.show({
                      color: "green",
                      message: "Prospect marked as contacted.",
                    });
                    statusModal.close();
                  })
                  .catch(() =>
                    notifications.show({
                      color: "red",
                      message: "The status could not be updated.",
                    })
                  );
              }}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};
